/** Human labels for enum values, in one place so every screen says the same thing. */
import type * as T from '@/api/types';

export const KIND_LABEL: Record<T.ListingKind, string> = {
  helicopter: 'Helicopter', airplane: 'Airplane', gyroplane: 'Gyroplane', engine: 'Engine', avionics: 'Avionics',
  part: 'Part', accessory: 'Accessory', service: 'Service', other: 'Other',
};

export const STATE_LABEL: Record<T.ListingState, string> = {
  draft: 'Draft', pending_review: 'In review', active: 'Live', paused: 'Paused', pending_sale: 'Sale pending',
  sold: 'Sold', expired: 'Expired', removed: 'Removed', rejected: 'Needs fixes',
};

export const REVIEW_LABEL: Record<T.ReviewStatus, string> = {
  pending: 'Reviewing', approved: 'Approved', limited: 'Limited', rejected: 'Rejected',
};

export const BASIS_LABEL: Record<T.BillingBasis, string> = {
  free_annual: 'Free (annual)', paid: 'Paid', policy_free: 'Free (policy)', grandfathered: 'Grandfathered',
};

export const LEAD_STATUS_LABEL: Record<T.LeadStatus, string> = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified', won: 'Won', lost: 'Lost', spam: 'Spam',
};

export const LEAD_SOURCE_LABEL: Record<T.LeadSource, string> = {
  message: 'App message', call_tap: 'Call tap', email_tap: 'Email tap', web_form: 'Web form', feed_card: 'Feed card', ad_click: 'Sponsored click',
};

export const CAMPAIGN_STATUS_LABEL: Record<T.CampaignStatus, string> = {
  draft: 'Draft', active: 'Active', paused: 'Paused', budget_exhausted: 'Out of budget', ended: 'Ended', rejected: 'Rejected',
};

/**
 * System pause reasons (set by billing/settle). Payment failure pauses promotion + brand campaigns at once;
 * listing presence fees get a 72h grace before the listing pauses; a failed auto-recharge isn't retried for 24h.
 */
export const PAUSE_REASON_LABEL: Record<string, { label: string; help: string; fixTo: string; fixLabel: string }> = {
  payment_failed: {
    label: 'Paused — payment failed',
    help: 'A payment failed, so promotion and brand campaigns were paused. Listing fees keep your listings live for a 72-hour grace period before they pause too.',
    fixTo: '/billing', fixLabel: 'Update card or add funds',
  },
  auto_recharge_failed: {
    label: 'Paused — auto-recharge failed',
    help: 'Your card was declined when we tried to top up your balance. We won’t retry for 24 hours. Listing fees get a 72-hour grace period before listings pause.',
    fixTo: '/billing', fixLabel: 'Fix payment method',
  },
  account_suspended: {
    label: 'Paused — account suspended',
    help: 'This account is suspended, so all campaigns are paused. Contact LowOps support to resolve it.',
    fixTo: '/billing', fixLabel: 'View billing',
  },
};

/** Human text for a campaign pause_reason (system code or free text). */
export function pauseReasonText(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return PAUSE_REASON_LABEL[reason]?.label ?? reason;
}

export const OBJECTIVE_LABEL: Record<T.CampaignObjective, string> = {
  presence: 'Listing presence', views: 'More views', leads: 'More leads', awareness: 'Brand awareness',
};

export const PLACEMENT_LABEL: Record<T.Placement, { label: string; hint: string }> = {
  feed: { label: 'LowOps app feed', hint: 'Every 8th post in the pilot community feed' },
  search_top: { label: 'App search — top', hint: 'Top of marketplace results in the app' },
  listing_similar: { label: 'App — similar listings', hint: 'Under comparable aircraft in the app' },
  web_search: { label: 'flylowops.com search', hint: 'Top of marketplace results on the web' },
  web_listing: { label: 'flylowops.com listing pages', hint: 'Beside comparable listings on the web' },
};

export const ROLE_LABEL: Record<T.MemberRole, { label: string; hint: string }> = {
  owner: { label: 'Owner', hint: 'Everything, including billing, team and deleting the account' },
  admin: { label: 'Admin', hint: 'Everything except removing owners' },
  sales: { label: 'Sales', hint: 'Listings and leads assigned to them or unassigned' },
  billing: { label: 'Billing', hint: 'Billing, cards, top-ups and receipts only' },
};

export const LEDGER_LABEL: Record<T.LedgerEntryType, string> = {
  topup: 'Top-up', credit_grant: 'Credit granted', spend: 'Spend', refund: 'Refund', credit_expiry: 'Credit expired',
  invalid_traffic_credit: 'Invalid-traffic credit', adjustment: 'Adjustment', chargeback: 'Chargeback',
};

export const PURPOSE_LABEL: Record<T.SpendPurpose, string> = {
  listing_fee: 'Listing fees', promotion: 'Promotion', brand: 'Brand ads',
};

export const MODE_LABEL: Record<T.PolicyMode, { short: string; label: string; hint: string }> = {
  listing_is_ad: { short: 'Mode A', label: 'Listing is the ad', hint: 'Individuals get 1 free listing per rolling year; everything else pays a daily listing fee.' },
  free_listing_paid_promotion: { short: 'Mode B', label: 'Free listings, paid promotion', hint: 'Listings are free (capped per account); promotion and brand ads are paid.' },
};

export const HOURS_BANDS = ['<500', '500-1500', '1500-5000', '5000+'];
export const SEGMENTS = ['hems', 'tour', 'utility', 'ag', 'law_enforcement', 'offshore', 'firefighting', 'corporate', 'training', 'military', 'private'];
export const CREDENTIALS = ['ATP', 'Commercial', 'Private', 'CFI', 'CFII', 'Instrument', 'Part 135 PIC', 'Long-line', 'NVG'];
export const TYPE_RATINGS = ['AS350', 'EC130', 'EC135', 'B206', 'B407', 'B412', 'R22', 'R44', 'R66', 'MD500', 'AW109', 'AW119', 'S76', 'UH-60'];
export const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export const CONDITION_LABEL: Record<T.LegacyCondition, string> = {
  new: 'New', used: 'Used', overhauled: 'Overhauled', for_parts: 'For parts',
};
